// jsx/components/Scoring/GuidelinesModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';

const getGuidelineContent = (criterionKey, matrixType) => {
  const guidelines = {
    sectional: {
      scientificLeadership: {
        title: 'Scientific Achievement',
        content: (
          <>
            <p>
              <strong>Scientific achievement:</strong> This dimension focuses on
              the impact of the candidate's research in the field (including the
              translation or applied impact of that research). Scientific
              excellence is based on contributions with major impact in the
              field. This may be either a single contribution, or multiple
              contributions with clear impact. For candidates whose research has
              been interrupted, their scientific achievement should be
              considered for the period in which they were active scientists
              plus ongoing citations.
            </p>
            <p>
              <strong>
                Normally score out of 60, but for singular exceptional
                contributions, this may be adjusted higher up to a maximum of
                85. (Criteria 1: 60-85)
              </strong>
            </p>
            <p>
              Indicators of impact may include but are not limited to: main
              publications especially the papers that best support the
              nomination; citation of those publications; 'textbook' science;
              patents; significant impact on practice or translation of the
              research; or other indicators relative to the standards for each
              discipline. For 'applied' candidates indicators may include a
              significant impact of their applications of scientific knowledge
              to the invention or development of new devices, constructions,
              products or processes.
            </p>
          </>
        ),
      },
      nationalProfile: {
        title: 'National and International profile',
        content: (
          <>
            <p>
              <strong>National and International profile:</strong> This
              dimension focuses on how others have responded to the candidate's
              collective achievements.
            </p>
            <p>
              <strong>
                Normally score out of 20, but may be adjusted in line with the
                conditions stated further below but must not be less than 10.
                (Criteria 2: 10-35)
              </strong>
            </p>
            <p>
              Indicators include: invitations to speak; grants and Fellowships;
              journal editorships; honours, awards and prizes; membership of
              prestigious organisations or committees; and supportive letters
              from referees.
            </p>
          </>
        ),
      },
      leadershipMentorship: {
        title: 'Leadership, Mentorship, Promotion of Science',
        content: (
          <>
            <p>
              <strong>
                Leadership, mentorship, promotion of science and potential to
                advance the work of the Academy:
              </strong>{' '}
              This dimension focuses on what the candidate has done other than
              their own research.
            </p>
            <p>
              <strong>
                Normally score out of 20, but may be adjusted in line with the
                conditions stated further below but must not be less than 5.
                (Criteria 3: 5-30)
              </strong>
            </p>
            <p>
              Indicators include: executive and leadership roles in science (not
              just within the specific research area of the candidate);
              conference organisation; high-level peer review functions; science
              education, outreach, advocacy and/or policy development;
              successful mentorship of the next generation of scientists; other
              contributions to the discipline.
            </p>
          </>
        ),
      },
    },
    corresponding: {
      scientificExcellence: {
        title: 'Scientific excellence',
        content: (
          <p>
            The primary selection criterion is scientific excellence through
            EITHER a single ground-breaking contribution to science, OR a
            cohesive body of smaller contributions with clear impact, as
            indicated by but not limited to: ground-breaking publications,
            citation of those publications, 'textbook' science, patents, major
            impact on practice or translation of the research, or other
            indicators relative to the standards for each discipline. (50%)
          </p>
        ),
      },
      internationalProfile: {
        title: 'International profile',
        content: (
          <p>
            Extremely high international research profile, including supportive
            letters of reference from eminent referees. (30%)
          </p>
        ),
      },
      connectionToAustralian: {
        title: 'Connection to Australian science',
        content: (
          <>
            <p>
              Strength of connection or strategic value to Australian science
              including potential to advance the work of the Academy in science
              education, advocacy or policy. (20%)
            </p>
            <p>
              <em>
                The connection to Australian science should be more extensive
                than conducting a collaborative research project or sharing
                supervision of one or two research students and may be broader
                than research activity.
              </em>
            </p>
          </>
        ),
      },
    },
    special: {
      serviceToScience: {
        title: 'Service to science',
        content: (
          <p>
            Evidence of sustained service to the cause of science and science
            leadership, including mentorship, at the highest level and/or
            evidence of innovation or entrepreneurial spirit through the
            translation of the results of scientific research that have led to
            global impact. (50%)
          </p>
        ),
      },
      nationalProfile: {
        title: 'National and International profile',
        content: (
          <p>
            Evidence of very high national and/or international profile,
            including supportive letters of reference from eminent referees.
            (30%)
          </p>
        ),
      },
      academyVision: {
        title: "Supporting the Academy's vision",
        content: (
          <p>
            Potential to advance the work of the Academy in science education,
            advocacy, policy or international engagement. (20%)
          </p>
        ),
      },
    },
  };

  return (
    guidelines[matrixType]?.[criterionKey] || {
      title: 'Guidelines',
      content: <p>Guidelines not available for this criterion.</p>,
    }
  );
};

const GuidelinesModal = ({
  show,
  onHide,
  criterionKey,
  matrixType = 'sectional',
}) => {
  const guideline = getGuidelineContent(criterionKey, matrixType);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Guidelines: {guideline.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="guidelines-content">{guideline.content}</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

GuidelinesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  criterionKey: PropTypes.string,
  matrixType: PropTypes.oneOf(['sectional', 'corresponding', 'special']),
};

export default GuidelinesModal;
